import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import SearchableSelect from '../SearchableSelect';
import MultiSearchableSelect from '../MultiSearchableSelect';
import { InfoDot } from '../hiring/HiringForm';
import { InlineLoader } from '../loading';
import { FIELD_INPUT } from './bannerFormBits';
import type { BannerAudience, DetailRuleDraft } from './useBannerAudience';

/** The server's limit on one send's rules. */
const MAX_RULES = 20;

const FIXED_AT_SEND =
  'The list is fixed at send: whoever fits these details then gets it. People who join later will not.';

const toOption = (value: string) => ({ value, label: value });

function DetailRuleRow({
  rule,
  audience,
  usedKeys,
}: {
  rule: DetailRuleDraft;
  audience: BannerAudience;
  /** Details the other rows have taken: one rule per detail, as values within it mean "any". */
  usedKeys: Set<string>;
}) {
  const { details, updateRule, removeRule } = audience;
  const field = details.fieldOf(rule.fieldKey);
  const [typed, setTyped] = useState('');
  // Values typed for a detail with more than the list holds stay on the list.
  const valueOptions = [...new Set([...(field?.values ?? []), ...rule.values])].map(toOption);
  const addTyped = () => {
    const value = typed.trim();
    if (value && !rule.values.includes(value)) updateRule(rule.id, { values: [...rule.values, value] });
    setTyped('');
  };

  return (
    <div>
      <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] items-center gap-2">
        <label htmlFor={`rule-${rule.id}-detail`} className="sr-only">
          Detail
        </label>
        <SearchableSelect
          id={`rule-${rule.id}-detail`}
          value={rule.fieldKey}
          onChange={(fieldKey) => updateRule(rule.id, { fieldKey, values: [] })}
          options={details.fieldOptions.filter((o) => o.value === rule.fieldKey || !usedKeys.has(o.value))}
          placeholder="Pick a detail"
          searchPlaceholder="Search details…"
          allowClear={false}
          emptyText={details.fieldOptions.length > 0 ? 'Every detail is in use' : 'This project has no details to pick from'}
        />
        <label htmlFor={`rule-${rule.id}-values`} className="sr-only">
          Values
        </label>
        <MultiSearchableSelect
          id={`rule-${rule.id}-values`}
          values={rule.values}
          onChange={(values) => updateRule(rule.id, { values })}
          options={valueOptions}
          placeholder="Pick values"
          searchPlaceholder="Search values…"
          disabled={!field}
        />
        <button
          type="button"
          onClick={() => removeRule(rule.id)}
          aria-label={`Remove ${field?.label ?? 'this detail'}`}
          title="Remove"
          className="flex h-6 w-6 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      {field?.valuesTruncated && (
        <input
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addTyped();
            }
          }}
          onBlur={addTyped}
          aria-label={`Type a value of ${field.label}`}
          placeholder={`First ${field.values.length} listed. Type another, then Enter`}
          className={`${FIELD_INPUT} mt-1.5`}
        />
      )}
    </div>
  );
}

/** Send to › By details: who fits these rules. Rules all have to fit; values within one mean any. */
export function DetailRulesField({ audience }: { audience: BannerAudience }) {
  const { selectedProjectId, details, ruleDrafts, addRule, matchCount, matchLoading, matchError, retryMatch } = audience;

  if (selectedProjectId == null) return <p className="text-[13px] text-gray-500">Pick a project to send by its details.</p>;
  if (details.error) {
    return (
      <p className="text-xs text-red-600">
        {details.error}{' '}
        <button type="button" onClick={details.retry} className="font-medium text-primary-600 underline hover:text-primary-700">
          Try again
        </button>
      </p>
    );
  }
  if (!details.loaded) return <InlineLoader label="Loading details…" />;
  if (details.fieldOptions.length === 0) return <p className="text-[13px] text-gray-500">This project has no details to send by yet.</p>;

  const used = (except: number) => new Set(ruleDrafts.filter((r) => r.id !== except).map((r) => r.fieldKey));

  return (
    <div className="flex flex-col gap-2">
      {ruleDrafts.map((rule) => (
        <DetailRuleRow key={rule.id} rule={rule} audience={audience} usedKeys={used(rule.id)} />
      ))}
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        {ruleDrafts.length < MAX_RULES && ruleDrafts.length < details.fieldOptions.length ? (
          <button
            type="button"
            onClick={addRule}
            className="flex w-fit items-center gap-1 text-[13px] font-medium text-primary-600 hover:text-primary-700"
          >
            <Plus className="h-3.5 w-3.5" />
            Add a detail
          </button>
        ) : (
          <span />
        )}
        <div aria-live="polite" className="flex items-center gap-1 text-[13px] text-gray-500">
          {matchLoading ? (
            <InlineLoader label="Counting…" className="flex" />
          ) : matchError ? (
            <span className="text-red-600">
              Could not count.{' '}
              <button type="button" onClick={retryMatch} className="font-medium text-primary-600 underline hover:text-primary-700">
                Try again
              </button>
            </span>
          ) : matchCount != null ? (
            <span>
              <strong className="text-sm font-semibold text-gray-900">{matchCount.toLocaleString()}</strong>{' '}
              {matchCount === 1 ? 'person matches' : 'people match'}
            </span>
          ) : null}
          <InfoDot text={FIXED_AT_SEND} />
        </div>
      </div>
      {/* A phone has no hover, and this one changes what someone would decide. */}
      <p className="text-xs text-gray-500 lg:hidden">People who join later won't get it.</p>
    </div>
  );
}
